import Tabs from '../../../components/AnimatedTabs';
import Table from "../../../components/RecordTable";
import { csvCell } from "../../../utils/records";
import React, { useState } from "react";
import {
  Card, Button, Space, Tag, DatePicker, Typography,
   Row, Col, Statistic, Empty, message, Collapse, Avatar,
} from "antd";
import {
  FileTextOutlined, CalendarOutlined, ExperimentOutlined,
  SearchOutlined, ReloadOutlined, DownloadOutlined, UserOutlined,
  MedicineBoxOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { appointmentAPI, testBookingAPI } from "../../../services/api";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ─── CSV utility ─────────────────────────────────────────────────────────────

const downloadCSV = (rows, filename) => {
  if (!rows.length) { message.warning("No data to download"); return; }
  const escape = csvCell;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.map(escape).join(","),
    ...rows.map((row) => headers.map((h) => escape(row[h])).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Lab Test Bookings Report ────────────────────────────────────────────────

const LabBookingsReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [fetched, setFetched] = useState(false);

  const handleDownload = () => {
    const rows = data.map((b, i) => {
      const tests = (b.tests || b.selectedTests || [])
        .map((t) => t.name || t.test?.name || t.testName || "Test")
        .join(" | ");
      const amt = b.payment?.totalAmount ?? b.totalAmount ?? b.amount ?? 0;
      const payStatus = b.payment?.status || b.paymentStatus || "";
      return {
        "S.No": i + 1,
        "Patient Name": b.patient?.name || "",
        "Email": b.patient?.email || "",
        "Phone": b.patient?.phone || "",
        "Lab Tests": tests,
        "Booking Date": b.createdAt ? dayjs(b.createdAt).format("DD MMM YYYY") : "",
        "Status": b.status || "",
        "Amount (₹)": amt,
        "Payment Status": payStatus,
      };
    });
    const range = dateRange?.[0] && dateRange?.[1]
      ? `${dateRange[0].format("YYYY-MM-DD")}_to_${dateRange[1].format("YYYY-MM-DD")}`
      : "all";
    downloadCSV(rows, `lab_bookings_${range}.csv`);
  };

  const fetch = async () => {
    setLoading(true);
    try {
      const params = { limit: 1000 };
      if (dateRange?.[0]) params.startDate = dateRange[0].startOf("day").toISOString();
      if (dateRange?.[1]) params.endDate = dateRange[1].endOf("day").toISOString();

      const res = await testBookingAPI.getAll(params);
      const bookings =
        res?.data?.data ||
        res?.data?.bookings ||
        (Array.isArray(res?.data) ? res.data : []);
      setData(bookings);
      setFetched(true);
    } catch {
      message.error("Failed to fetch lab bookings");
    } finally {
      setLoading(false);
    }
  };

  const totalRevenue = data.reduce(
    (sum, b) => sum + (b.payment?.totalAmount || b.totalAmount || 0),
    0
  );

  const columns = [
    {
      title: "#",
      key: "idx",
      width: 50,
      render: (_, __, i) => i + 1,
    },
    {
      title: "Patient",
      key: "patient",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.patient?.name || "N/A"}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.patient?.email}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.patient?.phone}</div>
        </div>
      ),
    },
    {
      title: "Lab Tests",
      key: "tests",
      render: (_, r) => {
        const tests = r.tests || r.selectedTests || [];
        if (!tests.length) return <Text type="secondary">—</Text>;
        return (
          <div>
            {tests.map((t, i) => (
              <Tag key={i} color="blue" style={{ marginBottom: 3 }}>
                {t.name || t.test?.name || t.testName || "Test"}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Booking Date",
      key: "date",
      render: (_, r) =>
        r.createdAt ? dayjs(r.createdAt).format("DD MMM YYYY") : "—",
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => (
        <Tag
          color={
            { completed: "green", pending: "orange", cancelled: "red", confirmed: "blue" }[
              s?.toLowerCase()
            ] || "default"
          }
        >
          {s?.toUpperCase() || "—"}
        </Tag>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      render: (_, r) => {
        const amt = r.payment?.totalAmount ?? r.totalAmount ?? r.amount ?? 0;
        return <Text strong style={{ color: "#1890ff" }}>₹{amt}</Text>;
      },
      sorter: (a, b) =>
        (a.payment?.totalAmount ?? a.totalAmount ?? 0) -
        (b.payment?.totalAmount ?? b.totalAmount ?? 0),
    },
    {
      title: "Payment",
      key: "payment",
      render: (_, r) => {
        const s = r.payment?.status || r.paymentStatus;
        return (
          <Tag
            color={{ paid: "green", pending: "orange", failed: "red" }[s?.toLowerCase()] || "default"}
          >
            {s?.toUpperCase() || "—"}
          </Tag>
        );
      },
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            onChange={setDateRange}
            value={dateRange}
            placeholder={["Start Date", "End Date"]}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={fetch}
            loading={loading}
          >
            Generate Report
          </Button>
          {fetched && (
            <>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                style={{ background: "#0A6EBD", borderColor: "#0A6EBD", color: "#fff" }}
              >
                Download CSV
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => { setData([]); setDateRange(null); setFetched(false); }}
              >
                Reset
              </Button>
            </>
          )}
        </Space>
      </Card>

      {fetched && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Total Bookings"
                  value={data.length}
                  prefix={<ExperimentOutlined />}
                  styles={{ content: { color: "#1890ff" } }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Loaded records · gross amount"
                  value={totalRevenue}
                  prefix="₹"
                  styles={{ content: { color: "#0A6EBD" } }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Completed"
                  value={data.filter((b) => b.status === "completed").length}
                  styles={{ content: { color: "#0A6EBD" } }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Pending"
                  value={data.filter((b) => b.status === "pending").length}
                  styles={{ content: { color: "#0A6EBD" } }}
                />
              </Card>
            </Col>
          </Row>

          <Card>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">
                {dateRange?.[0] && dateRange?.[1]
                  ? `${dateRange[0].format("DD MMM YYYY")} – ${dateRange[1].format("DD MMM YYYY")}`
                  : "All time"}
                {" · "}
                <Text strong>{data.length}</Text> bookings
              </Text>
            </div>
            <Table
              columns={columns}
              dataSource={data}
              rowKey={(r) => r._id || r.bookingNumber || Math.random()}
              loading={loading}
              pagination={{ pageSize: 15, showSizeChanger: false }}
              scroll={{ x: "max-content" }}
              locale={{ emptyText: <Empty description="No bookings in this range" /> }}
            />
          </Card>
        </>
      )}

      {!fetched && (
        <Card>
          <Empty
            image={<ExperimentOutlined style={{ fontSize: 48, color: "#bbb" }} />}
            description="Select a date range and click Generate Report"
          />
        </Card>
      )}
    </div>
  );
};

// ─── Doctor Appointments Report ───────────────────────────────────────────────

const AppointmentsReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [fetched, setFetched] = useState(false);

  const handleDownload = () => {
    const rows = data.map((a, i) => ({
      "S.No": i + 1,
      "Patient Name": a.patient?.name || "",
      "Patient Email": a.patient?.email || "",
      "Patient Phone": a.patient?.phone || "",
      "Doctor Name": a.doctor?.name || "",
      "Doctor Email": a.doctor?.email || "",
      "Appointment Date": a.appointmentDate ? dayjs(a.appointmentDate).format("DD MMM YYYY") : "",
      "Appointment Time": a.appointmentTime || "",
      "Status": a.status || "",
      "Fee (₹)": a.doctor?.fees || a.consultationFee || 0,
      "Payment Status": a.paymentStatus || "",
      "Reason": a.reason || "",
    }));
    const range = dateRange?.[0] && dateRange?.[1]
      ? `${dateRange[0].format("YYYY-MM-DD")}_to_${dateRange[1].format("YYYY-MM-DD")}`
      : "all";
    downloadCSV(rows, `appointments_${range}.csv`);
  };

  const fetch = async () => {
    setLoading(true);
    try {
      const params = { limit: 1000 };
      if (dateRange?.[0]) params.startDate = dateRange[0].startOf("day").toISOString();
      if (dateRange?.[1]) params.endDate = dateRange[1].endOf("day").toISOString();

      const res = await appointmentAPI.getAll(params);
      const appointments =
        res?.data?.data?.appointments ||
        res?.data?.appointments ||
        (Array.isArray(res?.data) ? res.data : []);
      setData(appointments);
      setFetched(true);
    } catch {
      message.error("Failed to fetch appointments");
    } finally {
      setLoading(false);
    }
  };

  const totalRevenue = data.reduce((sum, a) => sum + (a.doctor?.fees || a.consultationFee || 0), 0);

  const columns = [
    {
      title: "#",
      key: "idx",
      width: 50,
      render: (_, __, i) => i + 1,
    },
    {
      title: "Patient",
      key: "patient",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.patient?.name || "N/A"}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.patient?.email}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.patient?.phone}</div>
        </div>
      ),
    },
    {
      title: "Doctor",
      key: "doctor",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.doctor?.name || "N/A"}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.doctor?.email}</div>
        </div>
      ),
    },
    {
      title: "Appointment Date",
      key: "date",
      render: (_, r) => (
        <div>
          <div>{r.appointmentDate ? dayjs(r.appointmentDate).format("DD MMM YYYY") : "—"}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.appointmentTime || ""}</div>
        </div>
      ),
      sorter: (a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => (
        <Tag
          color={
            { pending: "orange", confirmed: "blue", completed: "green", cancelled: "red" }[
              s?.toLowerCase()
            ] || "default"
          }
        >
          {s?.toUpperCase() || "—"}
        </Tag>
      ),
    },
    {
      title: "Fee",
      key: "fee",
      render: (_, r) => {
        const fee = r.doctor?.fees || r.consultationFee || 0;
        return <Text strong style={{ color: "#1890ff" }}>₹{fee}</Text>;
      },
      sorter: (a, b) =>
        (a.doctor?.fees || a.consultationFee || 0) - (b.doctor?.fees || b.consultationFee || 0),
    },
    {
      title: "Payment",
      dataIndex: "paymentStatus",
      render: (s) => (
        <Tag
          color={{ paid: "green", pending: "orange", failed: "red" }[s?.toLowerCase()] || "default"}
        >
          {s?.toUpperCase() || "—"}
        </Tag>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            onChange={setDateRange}
            value={dateRange}
            placeholder={["Start Date", "End Date"]}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={fetch}
            loading={loading}
          >
            Generate Report
          </Button>
          {fetched && (
            <>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                style={{ background: "#0A6EBD", borderColor: "#0A6EBD", color: "#fff" }}
              >
                Download CSV
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => { setData([]); setDateRange(null); setFetched(false); }}
              >
                Reset
              </Button>
            </>
          )}
        </Space>
      </Card>

      {fetched && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Total Appointments"
                  value={data.length}
                  prefix={<CalendarOutlined />}
                  styles={{ content: { color: "#1890ff" } }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Loaded records · gross amount"
                  value={totalRevenue}
                  prefix="₹"
                  styles={{ content: { color: "#0A6EBD" } }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Completed"
                  value={data.filter((a) => a.status === "completed").length}
                  styles={{ content: { color: "#0A6EBD" } }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Pending"
                  value={data.filter((a) => a.status === "pending").length}
                  styles={{ content: { color: "#0A6EBD" } }}
                />
              </Card>
            </Col>
          </Row>

          <Card>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">
                {dateRange?.[0] && dateRange?.[1]
                  ? `${dateRange[0].format("DD MMM YYYY")} – ${dateRange[1].format("DD MMM YYYY")}`
                  : "All time"}
                {" · "}
                <Text strong>{data.length}</Text> appointments
              </Text>
            </div>
            <Table
              columns={columns}
              dataSource={data}
              rowKey="_id"
              loading={loading}
              pagination={{ pageSize: 15, showSizeChanger: false }}
              scroll={{ x: "max-content" }}
              locale={{ emptyText: <Empty description="No appointments in this range" /> }}
            />
          </Card>
        </>
      )}

      {!fetched && (
        <Card>
          <Empty
            image={<CalendarOutlined style={{ fontSize: 48, color: "#bbb" }} />}
            description="Select a date range and click Generate Report"
          />
        </Card>
      )}
    </div>
  );
};

// ─── Doctor Revenue Report ────────────────────────────────────────────────────

const DoctorRevenueReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [fetched, setFetched] = useState(false);

  const handleDownload = () => {
    const rows = [];
    data.forEach((d) => {
      (d.appointments || []).forEach((a, i) => {
        rows.push({
          "Doctor Name": d.doctorName,
          "Doctor Email": d.doctorEmail,
          "Doctor Phone": d.doctorPhone,
          "Patient Name": a.patient?.name || "",
          "Patient Phone": a.patient?.phone || "",
          "Appointment Date": a.appointmentDate ? dayjs(a.appointmentDate).format("DD MMM YYYY") : "",
          "Time": a.appointmentTime || "",
          "Status": a.status || "",
          "Fee (₹)": a.doctor?.fees || a.consultationFee || 0,
          "Payment Status": a.paymentStatus || "",
          "Booking ID": a.bookingId || "",
        });
      });
    });
    const range = dateRange?.[0] && dateRange?.[1]
      ? `${dateRange[0].format("YYYY-MM-DD")}_to_${dateRange[1].format("YYYY-MM-DD")}`
      : "all";
    downloadCSV(rows, `doctor_revenue_${range}.csv`);
  };

  const fetch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange?.[0]) params.startDate = dateRange[0].startOf("day").toISOString();
      if (dateRange?.[1]) params.endDate = dateRange[1].endOf("day").toISOString();

      const res = await appointmentAPI.getDoctorRevenue(params);
      const doctors = res?.data || (Array.isArray(res) ? res : []);
      setData(doctors);
      setFetched(true);
    } catch {
      message.error("Failed to fetch doctor revenue report");
    } finally {
      setLoading(false);
    }
  };

  const totalRevenue = data.reduce((sum, d) => sum + d.revenue, 0);
  const totalAppointments = data.reduce((sum, d) => sum + d.totalAppointments, 0);
  const totalClients = data.reduce((sum, d) => sum + d.uniqueClients, 0);

  const logColumns = [
    { title: "#", key: "idx", width: 48, render: (_, __, i) => i + 1 },
    {
      title: "Patient",
      key: "patient",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.patient?.name || "N/A"}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.patient?.phone}</div>
        </div>
      ),
    },
    {
      title: "Date & Time",
      key: "date",
      render: (_, r) => (
        <div>
          <div>{r.appointmentDate ? dayjs(r.appointmentDate).format("DD MMM YYYY") : "—"}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.appointmentTime || ""}</div>
        </div>
      ),
      sorter: (a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => (
        <Tag color={{ pending: "orange", confirmed: "blue", completed: "green", cancelled: "red" }[s] || "default"}>
          {s?.toUpperCase() || "—"}
        </Tag>
      ),
    },
    {
      title: "Fee",
      key: "fee",
      render: (_, r) => <Text strong style={{ color: "#1890ff" }}>₹{r.doctor?.fees || r.consultationFee || 0}</Text>,
    },
    {
      title: "Booking ID",
      dataIndex: "bookingId",
      render: (v) => v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : <Text type="secondary">—</Text>,
    },
  ];

  const collapseItems = data.map((d) => ({
    key: d.doctorId?.toString() || d.doctorName,
    label: (
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Avatar icon={<UserOutlined />} style={{ background: "#1890ff" }} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{d.doctorName}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{d.doctorEmail}</div>
        </div>
        <Space size={24} style={{ marginLeft: "auto" }}>
          <Statistic
            title="Revenue"
            value={d.revenue}
            prefix="₹"
            styles={{ content: { color: "#0A6EBD", fontSize: 16 } }}
          />
          <Statistic
            title="Appointments"
            value={d.totalAppointments}
            styles={{ content: { fontSize: 16 } }}
          />
          <Statistic
            title="Completed"
            value={d.completedAppointments}
            styles={{ content: { color: "#0A6EBD", fontSize: 16 } }}
          />
          <Statistic
            title="Clients"
            value={d.uniqueClients}
            styles={{ content: { fontSize: 16 } }}
          />
        </Space>
      </div>
    ),
    children: (
      <Table
        columns={logColumns}
        dataSource={d.appointments || []}
        rowKey="_id"
        pagination={{ pageSize: 10, showSizeChanger: false }}
        scroll={{ x: "max-content" }}
        size="small"
        locale={{ emptyText: <Empty description="No appointments" /> }}
      />
    ),
  }));

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            onChange={setDateRange}
            value={dateRange}
            placeholder={["Start Date", "End Date"]}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={fetch}
            loading={loading}
          >
            Generate Report
          </Button>
          {fetched && (
            <>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                style={{ background: "#0A6EBD", borderColor: "#0A6EBD", color: "#fff" }}
              >
                Download Invoice CSV
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => { setData([]); setDateRange(null); setFetched(false); }}
              >
                Reset
              </Button>
            </>
          )}
        </Space>
      </Card>

      {fetched && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Doctors"
                  value={data.length}
                  prefix={<MedicineBoxOutlined />}
                  styles={{ content: { color: "#1890ff" } }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Loaded records · gross amount"
                  value={totalRevenue}
                  prefix="₹"
                  styles={{ content: { color: "#0A6EBD" } }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Total Appointments"
                  value={totalAppointments}
                  prefix={<CalendarOutlined />}
                  styles={{ content: { color: "#1890ff" } }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Unique Clients"
                  value={totalClients}
                  prefix={<UserOutlined />}
                  styles={{ content: { color: "#0A6EBD" } }}
                />
              </Card>
            </Col>
          </Row>

          {data.length > 0 ? (
            <Collapse items={collapseItems} accordion={false} />
          ) : (
            <Card>
              <Empty description="No appointment data in this range" />
            </Card>
          )}
        </>
      )}

      {!fetched && (
        <Card>
          <Empty
            image={<MedicineBoxOutlined style={{ fontSize: 48, color: "#bbb" }} />}
            description="Select a date range and click Generate Report"
          />
        </Card>
      )}
    </div>
  );
};

// ─── Main Reports Page ────────────────────────────────────────────────────────

const Reports = () => (
  <div>
    <div style={{ marginBottom: 24 }}>
      <Title level={2} style={{ margin: 0 }}>
        <FileTextOutlined style={{ marginRight: 10 }} />
        Reports
      </Title>
      <Text type="secondary">Generate date-range reports for lab bookings and doctor appointments</Text>
    </div>

    <Tabs
      defaultActiveKey="lab"
      size="large"
      items={[
        {
          key: "lab",
          label: (
            <span>
              <ExperimentOutlined />
              Lab Test Bookings
            </span>
          ),
          children: <LabBookingsReport />,
        },
        {
          key: "appointments",
          label: (
            <span>
              <CalendarOutlined />
              Doctor Appointments
            </span>
          ),
          children: <AppointmentsReport />,
        },
        {
          key: "doctor-revenue",
          label: (
            <span>
              <MedicineBoxOutlined />
              Doctor Revenue
            </span>
          ),
          children: <DoctorRevenueReport />,
        },
      ]}
    />
  </div>
);

export default Reports;
